import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import {
  InventoryLimit,
  useBulkDeleteOfferInventoryLimits,
  useDeleteOfferInventoryLimits,
  useGetOfferInventoryLimits,
  useSetBulkOfferInventoryLimits,
  useSetOfferInventoryLimits,
} from '~/hooks/apis/reservation/useOfferInventoryLimits';

import roomRateName from '~/utils/roomRateName';

import OfferInventoryCalendar from './OfferInventoryCalendar';
import OfferInventoryScopeChoice from './OfferInventoryScopeChoice';

interface Props {
  offerId: string;
  propertyId: string;
  roomRates: Array<App.RoomRate>;
  roomType: App.RoomType;
}

function sortedDateArray(dates: Set<string>): Array<string> {
  return Array.from(dates).sort();
}

export default function OfferInventoryPane({ offerId, propertyId, roomType, roomRates }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [selectMode, setSelectMode] = useState<'free' | 'range'>('range');

  const { data: limitsResponse, isLoading } = useGetOfferInventoryLimits(offerId, propertyId, roomType.id);
  const mode = limitsResponse?.mode ?? null;

  // While mode is null (no caps saved yet for this room type), the user picks a
  // scope up front via OfferInventoryScopeChoice rather than discovering it
  // implicitly through tabs. Once mode resolves server-side (first cap saved), it
  // always wins over this provisional choice.
  const [nullModeChoice, setNullModeChoice] = useState<'room_rate' | 'room_type' | null>(null);
  const effectiveMode = mode ?? nullModeChoice;

  const allLimits = useMemo(() => limitsResponse?.limits ?? [], [limitsResponse]);

  const limitsByRoomRateId = useMemo(() => {
    const map = new Map<string, Array<InventoryLimit>>();
    for (const limit of allLimits) {
      if (!limit.room_rate_id) {
        continue;
      }
      const existing = map.get(limit.room_rate_id);
      if (existing) {
        existing.push(limit);
      } else {
        map.set(limit.room_rate_id, [limit]);
      }
    }
    return map;
  }, [allLimits]);

  const [selectedRoomRateId, setSelectedRoomRateId] = useState<string | undefined>(undefined);

  // Room-type mode ignores any stale room-rate sub-tab selection - there's only one
  // scope to view once room-type mode is locked in.
  const activeRoomRateId = effectiveMode === 'room_type' ? undefined : selectedRoomRateId;

  // Once this room type flips into room-rate mode (server-confirmed or
  // provisionally chosen), default to its first rate so the panel shows something
  // immediately rather than an empty "pick a rate" state.
  useEffect(() => {
    if (effectiveMode !== 'room_rate' || selectedRoomRateId) {
      return;
    }
    const firstRoomRateId = roomRates[0]?.id;
    if (firstRoomRateId) {
      setSelectedRoomRateId(firstRoomRateId);
    }
  }, [effectiveMode, selectedRoomRateId, roomRates]);

  const inventoryLimits = useMemo(
    () => (effectiveMode === 'room_type' ? allLimits : (limitsByRoomRateId.get(activeRoomRateId ?? '') ?? [])),
    [effectiveMode, allLimits, limitsByRoomRateId, activeRoomRateId],
  );

  const selectedRoomRate = useMemo(
    () => roomRates.find((roomRate) => roomRate.id === activeRoomRateId),
    [roomRates, activeRoomRateId],
  );
  const scopeLabel = selectedRoomRate ? roomRateName(roomType, selectedRoomRate) : 'All rates (room type cap)';

  const setInventoryMutation = useSetOfferInventoryLimits(offerId, propertyId, roomType.id, activeRoomRateId);
  const setBulkInventoryMutation = useSetBulkOfferInventoryLimits(offerId, propertyId, roomType.id, activeRoomRateId);
  // Scoped to the active rate (or room-type, when unset) - used to clear only what's
  // currently in view. A separate, unscoped instance below backs "Clear all limits".
  const deleteInventoryMutation = useDeleteOfferInventoryLimits(offerId, propertyId, roomType.id, activeRoomRateId);
  const deleteAllInventoryMutation = useDeleteOfferInventoryLimits(offerId, propertyId, roomType.id);
  const bulkDeleteInventoryMutation = useBulkDeleteOfferInventoryLimits(
    offerId,
    propertyId,
    roomType.id,
    activeRoomRateId,
  );

  const selectedSorted = sortedDateArray(selectedDates);
  const selectionStart = selectedSorted[0] ?? '';
  const selectionEnd = selectedSorted[selectedSorted.length - 1] ?? '';

  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  const [inventoryTotal, setInventoryTotal] = useState('');
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [confirmClearRateOpen, setConfirmClearRateOpen] = useState(false);

  const handleSetInventory = async () => {
    const totalNum = parseInt(inventoryTotal, 10);
    if (!selectionStart || isNaN(totalNum) || totalNum < 0) {
      return;
    }
    try {
      if (selectMode === 'range') {
        await setInventoryMutation.mutateAsync({ start_date: selectionStart, end_date: selectionEnd, total: totalNum });
      } else {
        await setBulkInventoryMutation.mutateAsync({ dates: selectedSorted, total: totalNum });
      }
      enqueueSnackbar('Inventory limits updated', { variant: 'success' });
      setInventoryDialogOpen(false);
      setInventoryTotal('');
      setSelectedDates(new Set());
    } catch {
      enqueueSnackbar('Failed to set inventory limits', { variant: 'error' });
    }
  };

  const runClearMutation = async (
    mutation: { mutateAsync: (variables: undefined) => Promise<unknown> },
    onCleared: () => void,
  ) => {
    try {
      await mutation.mutateAsync(undefined);
      enqueueSnackbar('Inventory limits cleared', { variant: 'success' });
      onCleared();
    } catch {
      enqueueSnackbar('Failed to clear inventory limits', { variant: 'error' });
    }
  };

  const handleClearAllInventory = () =>
    runClearMutation(deleteAllInventoryMutation, () => {
      setConfirmClearOpen(false);
      setNullModeChoice(null);
    });

  // Clears every date for the currently-active scope only
  // passing no dates deletes all rows matching offer + room type + room_rate_id
  const handleClearRateInventory = () =>
    runClearMutation(deleteInventoryMutation, () => setConfirmClearRateOpen(false));

  const handleClearInventory = async (dates: Array<string>) => {
    try {
      if (selectMode === 'range') {
        const sorted = [...dates].sort();
        await deleteInventoryMutation.mutateAsync({ start_date: sorted[0], end_date: sorted[sorted.length - 1] });
      } else {
        await bulkDeleteInventoryMutation.mutateAsync(dates);
      }
      enqueueSnackbar('Inventory limits cleared', { variant: 'success' });
      setSelectedDates(new Set());
    } catch {
      enqueueSnackbar('Failed to clear inventory limits', { variant: 'error' });
    }
  };

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  return (
    <Stack spacing={3}>
      {allLimits.length > 0 && effectiveMode !== 'room_rate' && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {allLimits.length} inventory limit{allLimits.length !== 1 ? 's' : ''} set
          </Typography>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setConfirmClearOpen(true)}
            disabled={deleteAllInventoryMutation.isPending}
          >
            Clear all limits
          </Button>
        </Stack>
      )}

      {effectiveMode === null && (
        <OfferInventoryScopeChoice roomTypeName={roomType.name} onChoose={setNullModeChoice} />
      )}

      {effectiveMode !== null && (
        <>
          {roomRates.length > 0 && effectiveMode === 'room_rate' && (
            <Tabs
              value={activeRoomRateId ?? roomRates[0]?.id ?? ''}
              onChange={(_, v) => {
                setSelectedRoomRateId(v);
                setSelectedDates(new Set());
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              {roomRates.map((roomRate) => (
                <Tab key={roomRate.id} label={roomRateName(roomType, roomRate)} value={roomRate.id} />
              ))}
            </Tabs>
          )}

          <Alert severity="info">
            {effectiveMode === 'room_type'
              ? 'This room type is capped as a whole: the total below applies across every rate combined.'
              : 'This room type is capped per rate: select a rate tab above to view or edit its own cap. A rate with no cap of its own is uncapped, even if sibling rates are capped.'}
            {mode === null && (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setNullModeChoice(null)}
                sx={{ ml: 1 }}
              >
                Change scope
              </Link>
            )}
          </Alert>

          {inventoryLimits.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {effectiveMode === 'room_rate'
                ? `No inventory cap set for ${scopeLabel}. This rate has no cap of its own and is currently uncapped (unlimited).`
                : `No inventory cap set yet for ${scopeLabel}.`}
            </Typography>
          )}

          {activeRoomRateId && inventoryLimits.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {inventoryLimits.length} limit{inventoryLimits.length !== 1 ? 's' : ''} set for {scopeLabel}
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setConfirmClearRateOpen(true)}
                disabled={deleteInventoryMutation.isPending}
              >
                Clear this rate's limits
              </Button>
            </Stack>
          )}

          <OfferInventoryCalendar
            inventoryLimits={inventoryLimits}
            selectedDates={selectedDates}
            selectMode={selectMode}
            onSelectModeChange={setSelectMode}
            onDatesChange={setSelectedDates}
            onSetInventory={() => {
              setInventoryTotal('');
              setInventoryDialogOpen(true);
            }}
            onClearInventory={(dates) => handleClearInventory(dates)}
          />
        </>
      )}

      <Dialog open={inventoryDialogOpen} onClose={() => setInventoryDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {inventoryLimits.some((l) => selectedDates.has(l.date)) ? 'Update Inventory Limits' : 'Set Inventory Limits'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Alert severity="info">
              Applying to {selectedDates.size} date{selectedDates.size !== 1 ? 's' : ''}: {selectionStart}
              {selectionStart !== selectionEnd ? ` – ${selectionEnd}` : ''}
              <br />
              Scope: {scopeLabel}
            </Alert>
            <TextField
              label="Total rooms available"
              type="number"
              value={inventoryTotal}
              onChange={(e) => setInventoryTotal(e.target.value)}
              inputProps={{ min: 0 }}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInventoryDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSetInventory}
            variant="contained"
            disabled={setInventoryMutation.isPending || setBulkInventoryMutation.isPending || inventoryTotal === ''}
          >
            {setInventoryMutation.isPending || setBulkInventoryMutation.isPending ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear all inventory limits?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete all {allLimits.length} inventory limit
            {allLimits.length !== 1 ? 's' : ''} for this room type, across every rate. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button
            onClick={handleClearAllInventory}
            variant="contained"
            color="error"
            disabled={deleteAllInventoryMutation.isPending}
          >
            {deleteAllInventoryMutation.isPending ? 'Clearing…' : 'Clear all'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClearRateOpen} onClose={() => setConfirmClearRateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear inventory limits for {scopeLabel}?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete all {inventoryLimits.length} inventory limit
            {inventoryLimits.length !== 1 ? 's' : ''} for {scopeLabel} only - sibling rates are unaffected. This cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearRateOpen(false)}>Cancel</Button>
          <Button
            onClick={handleClearRateInventory}
            variant="contained"
            color="error"
            disabled={deleteInventoryMutation.isPending}
          >
            {deleteInventoryMutation.isPending ? 'Clearing…' : 'Clear rate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
